import { useCallback, useContext, useEffect } from "react";

import { AgeGroup } from "@/components/ChooseAgeGroup/utils";
import { ActivityLevels } from "@/constants/activityLevel";
import { FemaleBodyFatRangeTypes, MaleBodyFatRangeTypes, UnitTypes } from "@/constants/bodyMetrics";
import { BodyTypes } from "@/constants/bodyType";
import { FitnessGoals } from "@/constants/fitnessGoal";
import { FitnessLevels } from "@/constants/fitnessLevels";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import {
    BadHabitsAnswers,
    CoachResponseAnswers,
    EventAnswers,
    PaceAnswers,
    TimeForYourselfAnswers,
    TypicalDayAnswers,
    WeightTypicalChangeAnswers,
    YesNoAnswers,
} from "@/types/answers";

import { Answers, AnswersContext } from "./AnswersProvider";
import { localStorageManager, LocalStorageManagerKeys } from "./localStorageManager";

type ValuesTypes = {
    [QUESTIONS_IDS.PRIMARY_GOAL]: FitnessGoals;
    [QUESTIONS_IDS.AGE]: number;
    [QUESTIONS_IDS.FITNESS_LEVEL]: FitnessLevels;
    [QUESTIONS_IDS.EVENT_MOTIVATION]: EventAnswers;
    [QUESTIONS_IDS.LOCATION]: string;
    [QUESTIONS_IDS.PLAN_FAILED]: string;
    [QUESTIONS_IDS.HEIGHT]: number;
    [QUESTIONS_IDS.CURRENT_WEIGHT]: number;
    [QUESTIONS_IDS.TARGET_WEIGHT]: number;
    [QUESTIONS_IDS.PACE]: PaceAnswers;
    [QUESTIONS_IDS.FIRST_WEIGHT_PREDICT_DATE]: Date;
    [QUESTIONS_IDS.WEIGHT_LOSS_GOAL]: string;
    [QUESTIONS_IDS.FITNESS_INVESTMENT_AMOUNT]: number | null;
    [QUESTIONS_IDS.EVENT_DATE]: Date | "skip";
    [QUESTIONS_IDS.ABILITY_TO_STAY_MOTIVATED]: string;
    [QUESTIONS_IDS.DESIRED_BODY]: string;
    [QUESTIONS_IDS.WEIGHT_PREDICT_DATE]: Date;
    [QUESTIONS_IDS.INTERMEDIATE_WEIGHT_LOSS]: number;
    [QUESTIONS_IDS.INTERMEDIATE_WEIGHT_LOSS_DATE]: string;
    [QUESTIONS_IDS.SHOW_INTERMEDIATE_RESULT]: boolean;
    [QUESTIONS_IDS.ACTIVITY_LEVEL]: ActivityLevels;
    [QUESTIONS_IDS.BODY_FAT]: MaleBodyFatRangeTypes | FemaleBodyFatRangeTypes;
    [QUESTIONS_IDS.TYPICAL_DAY]: TypicalDayAnswers;
    [QUESTIONS_IDS.UNITS]: UnitTypes;
    [QUESTIONS_IDS.BODY_TYPE]: BodyTypes;
    [QUESTIONS_IDS.WEIGHT_TYPICAL_CHANGE]: WeightTypicalChangeAnswers;
    [QUESTIONS_IDS.BMI_RESULTS]: number;
    [QUESTIONS_IDS.AGE_GROUP]: AgeGroup | null;
    [QUESTIONS_IDS.TIME_FOR_YOURSELF]: TimeForYourselfAnswers;
    [QUESTIONS_IDS.BAD_HABITS]: BadHabitsAnswers[];
    [QUESTIONS_IDS.EATING_WITH_STRESS]: YesNoAnswers;
    [QUESTIONS_IDS.STRUGGLE_TO_RESIST_CERTAIN_FOODS]: YesNoAnswers;
    [QUESTIONS_IDS.STRUGGLE_TO_CHOOSE_WORKOUTS]: YesNoAnswers;
    [QUESTIONS_IDS.STRUGGLE_TO_STAY_CONSISTENT]: YesNoAnswers;
    [QUESTIONS_IDS.CHOOSE_COACH]: CoachResponseAnswers;
};

const useAnswers = () => {
    const { answers, ...rest } = useContext(AnswersContext);

    const getAnswersValue = useCallback(
        // TODO: remove when move all QUESTIONS_IDS to ValueTypes
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <T extends QUESTIONS_IDS>(questionId: T): ValuesTypes[T] => {
            if (Array.isArray(answers[questionId])) {
                // TODO: remove when move all QUESTIONS_IDS to ValueTypes
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return answers[questionId].join(", ") as ValuesTypes[T];
            }
            // TODO: remove when move all QUESTIONS_IDS to ValueTypes
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return answers[questionId] as ValuesTypes[T];
        },
        [answers]
    );

    return {
        answers,
        getAnswersValue,
        ...rest,
    };
};

export const useStorageAnswers = () => {
    const { setAnswers } = useAnswers();

    useEffect(() => {
        const savedAnswers = localStorageManager.getStoredItem(
            LocalStorageManagerKeys.ANSWERS
        ) as Answers;

        if (savedAnswers) {
            setAnswers(savedAnswers);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};

export default useAnswers;
